import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building, Users, ArrowRight, Shield, Sparkles } from 'lucide-react';

const ModernCareerWidget = () => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div className="w-full bg-white dark:bg-gray-900 py-20 px-6 md:px-12 relative">
      {/* Premium background pattern */}
      <div className="absolute inset-0 overflow-hidden opacity-5 dark:opacity-10">
        <div className="absolute w-full h-full">
          {[...Array(40)].map((_, index) => (
            <motion.div
              key={index}
              className="absolute bg-gray-900 dark:bg-amber-400 rounded-full"
              initial={{
                width: Math.random() * 8 + 2,
                height: Math.random() * 8 + 2,
                x: Math.random() * 100 + '%',
                y: Math.random() * 100 + '%',
                opacity: Math.random() * 0.5 + 0.3,
              }}
              animate={{
                y: [null, "-20px", null],
                opacity: [null, Math.random() * 0.3 + 0.5, null],
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                repeatType: "reverse",
                delay: Math.random() * 5,
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Main content container */}
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left side content */}
          <div className="w-full lg:w-1/2">
            {/* Section badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 mb-6"
            >
              <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium">Career Opportunities</span>
            </motion.div>
            
            {/* Main heading */}
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6 leading-tight"
            >
              Build Your Future With <span className="text-amber-600 dark:text-amber-600">Dev Bhoomi Group Companies</span>
            </motion.h2>
            
            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-gray-600 dark:text-gray-300 mb-8 text-lg"
            >
              Join our dynamic team of professionals and contribute to innovative solutions that make a difference.
            </motion.p>
            
            {/* Stats cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
            >
              {/* Employees card */}
              <motion.div
                whileHover={{ y: -5, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)" }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-amber-100 dark:bg-amber-900 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold text-gray-900 dark:text-gray-100">600+</h3>
                    <p className="text-gray-500 dark:text-gray-400">Employees</p>
                  </div>
                </div>
              </motion.div>
              
              {/* Openings card */}
              <motion.div
                whileHover={{ y: -5, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)" }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-amber-100 dark:bg-amber-900 p-3 rounded-lg">
                    <Building className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Openings</h3>
                    <p className="text-gray-500 dark:text-gray-400">Updated regularly</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Warning notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-gray-800/80 border-l-4 border-amber-400 dark:border-amber-500 rounded mb-8"
            >
              <Shield className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 dark:text-amber-200 text-sm">
                <span className="font-semibold">Important:</span> Beware of job scams. We do not charge or accept any amount or security deposit from job seekers.
              </p>
            </motion.div>
            
            {/* CTA Button */}
            <motion.a
              href="/careers"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-800 text-white font-medium rounded-lg shadow-lg hover:shadow-amber-200 dark:hover:shadow-amber-900/50 transition-all duration-300"
            >
              <span>Explore Opportunities</span>
              <motion.div
                animate={{ x: isHovered ? 5 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ArrowRight className="h-5 w-5" />
              </motion.div>
            </motion.a>
          </div>
          
          {/* Right side - Image and overlay elements */}
          <div className="w-full lg:w-1/2">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              {/* Main image with frame */}
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-gray-800 p-2">
                <div className="overflow-hidden rounded-xl">
                  <img 
                    src="https://img.freepik.com/free-photo/checking-data-laptop_1098-17026.jpg?uid=R158437386&ga=GA1.1.516298163.1739876026&semt=ais_hybrid&w=740" 
                    alt="Dev Bhoomi Group Companies team members" 
                    className="w-full h-auto object-cover rounded-xl"
                  />
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-5 -left-5 w-20 h-20 bg-amber-100 dark:bg-amber-900/70 rounded-lg z-0"></div>
              <div className="absolute -bottom-5 -right-5 w-20 h-20 bg-amber-600 dark:bg-amber-600 rounded-lg z-0"></div>
              
              {/* Floating card 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="absolute -bottom-10 left-10 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl z-20"
              >
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 border-2 border-white dark:border-gray-800 flex items-center justify-center">
                      <span className="text-amber-600 dark:text-amber-400 font-bold text-xs">DB</span>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 border-2 border-white dark:border-gray-800 flex items-center justify-center">
                      <span className="text-amber-600 dark:text-amber-300 font-bold text-xs">HR</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Join Our Team</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Diverse & Inclusive</p>
                  </div>
                </div>
              </motion.div>
              
              {/* Floating card 2 */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="absolute top-10 -right-5 bg-white dark:bg-gray-800 rounded-lg p-3 shadow-lg z-20"
              >
                <div className="flex items-center gap-2">
                  <div className="bg-amber-100 dark:bg-amber-900 p-2 rounded">
                    <Building className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Dev Bhoomi Group Companies</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernCareerWidget;